import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { Button, Card, Descriptions, Spin, Tabs, Tag, Timeline, Typography, List } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { getContactById, getContactDetailQueryKey } from '../api/contacts.client.js';

const { Title, Text } = Typography;

export function ContactDetailPage({ contactId }: { contactId: string }): React.ReactElement {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const [activeTab, setActiveTab] = useState<string>('info');

  const contactQuery = useQuery({
    queryKey: getContactDetailQueryKey(workspaceId, contactId),
    queryFn: () => getContactById(contactId),
    enabled: scope !== null,
  });

  if (contactQuery.isLoading) {
    return (
      <main className="mx-auto flex h-[80vh] items-center justify-center w-full max-w-5xl p-6">
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>Загрузка контакта...</Text>
      </main>
    );
  }

  if (contactQuery.isError || !contactQuery.data) {
    return (
      <main className="mx-auto flex w-full max-w-4xl p-6 h-[80vh] items-center justify-center">
        <Card style={{ textAlign: 'center', maxWidth: 480, width: '100%' }}>
          <Title level={4}>Контакт не найден</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {contactQuery.error?.message ?? 'Попробуйте открыть карточку ещё раз.'}
          </Text>
          <Button type="primary" onClick={() => void contactQuery.refetch()}>Повторить</Button>
        </Card>
      </main>
    );
  }

  const contact = contactQuery.data;

  const tabItems = [
    {
      key: 'info',
      label: 'Информация',
      children: (
        <div className="flex flex-col gap-5">
          <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <Descriptions title="Контактная информация" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Источник" span={2}>
                <Text strong data-testid="contact-summary-source">{contact.source ?? 'Не указан'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Теги" span={2}>
                {contact.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {contact.tags.map((tag) => (
                      <Tag key={tag} style={{ margin: 0 }}>{tag}</Tag>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">Нет тегов</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          
          <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <Title level={5} style={{ marginBottom: 16 }}>Заметки</Title>
            {contact.notes ? (
              <Text style={{ whiteSpace: 'pre-wrap' }}>{contact.notes}</Text>
            ) : (
              <Text type="secondary">Заметок пока нет.</Text>
            )}
          </Card>
        </div>
      ),
    },
    {
      key: 'deals',
      label: 'Сделки',
      children: (
        <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <Title level={5} style={{ marginBottom: 16 }}>Сделки контакта</Title>
          <List
            itemLayout="horizontal"
            dataSource={contact.deals}
            locale={{ emptyText: 'Нет привязанных сделок.' }}
            renderItem={(deal) => (
              <List.Item
                style={{ padding: '16px' }}
                className="hover:bg-gray-50 transition-colors"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <Link to={`/deals/${deal.id}`} style={{ color: '#d97706', fontWeight: 600 }}>{deal.title}</Link>
                    <div>
                      <Text type="secondary" style={{ fontSize: 13, marginRight: 8 }}>Стадия:</Text>
                      <Tag color="default">{deal.stage}</Tag>
                    </div>
                  </div>
                  <Text strong style={{ fontFamily: 'monospace' }}>{deal.amount} ₽</Text>
                </div>
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: 'activity',
      label: 'История',
      children: (
        <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <Title level={5} style={{ marginBottom: 24 }}>История</Title>
          {contact.interactions.length > 0 ? (
            <Timeline
              items={contact.interactions.map((interaction, idx) => ({
                color: idx === 0 ? 'green' : 'gray',
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block' }}>{interaction.subject}</Text>
                    {interaction.body ? (
                      <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>{interaction.body}</Text>
                    ) : null}
                  </div>
                ),
              }))}
            />
          ) : (
            <Text type="secondary">История событий пуста.</Text>
          )}
        </Card>
      ),
    },
  ];

  return (
    <main
      className="flex flex-col p-6 mx-auto w-full h-full min-h-screen"
      style={{
        backgroundColor: '#f9f7f4',
        maxWidth: 1200,
        gap: 24,
      }}
    >
      <Card
        bordered={false}
        styles={{ body: { padding: '24px 24px 20px' } }}
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-3">
            <Button
              type="link"
              href="/contacts"
              aria-label="К контактам"
              icon={<ArrowLeftOutlined />}
              style={{ padding: 0, height: 'auto', display: 'flex', alignSelf: 'flex-start', color: '#d97706' }}
            >
              К контактам
            </Button>
            <div className="flex flex-col gap-1">
              <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#44403c' }}>
                {contact.fullName}
              </Title>
              <Text type="secondary" style={{ fontSize: 14 }}>
                {contact.email ?? contact.phone ?? 'Канал связи не указан'}
              </Text>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button size="large">Редактировать</Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col min-w-0">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="ant-tabs-warm"
          />
        </div>

        <aside className="hidden lg:flex flex-col gap-6">
          <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)', backgroundColor: '#fffdfa' }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', color: '#b45309', marginBottom: 8 }}>
              Активные сделки
            </Text>
            <Text strong data-testid="contact-summary-deals" style={{ fontSize: 32, display: 'block', color: '#92400e', fontFamily: 'monospace' }}>
              {contact.dealsCount}
            </Text>
            {contact.deals.map((deal) => (
              <Link key={deal.id} to={`/deals/${deal.id}`} style={{ display: 'block', marginTop: 12, color: '#d97706', fontWeight: 600 }}>
                {deal.title}
              </Link>
            ))}
          </Card>
          <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)', backgroundColor: '#fffdfa' }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', color: '#b45309', marginBottom: 8 }}>
              Последняя активность
            </Text>
            {contact.interactions.map((interaction) => (
              <div key={interaction.id} style={{ marginTop: 8 }}>
                <Text strong style={{ display: 'block' }}>{interaction.subject}</Text>
                {interaction.body ? <Text type="secondary">{interaction.body}</Text> : null}
              </div>
            ))}
          </Card>
        </aside>
      </div>
    </main>
  );
}
